import AuthenticatedImage from 'src/components/Items/AuthenticatedImage';
import ChildrenListSelect from 'src/components/Selects/ChildrenListSelect';
import Clamp from 'react-multiline-clamp';
import CloseButton from 'src/components/Buttons/CloseButton';
import Currency from 'react-currency-formatter';
import DateFormat from 'src/utils/dateFormat';
import IconButton from 'src/components/Buttons/IconButton';
import Modal from '../../../utils/modal';
import moment from 'src/utils/moment';
import NormalButton from 'src/components/Buttons/NormalButton';
import React, { useCallback, useMemo, useRef } from 'react';
import SVG from 'src/components/Images/SvgRenderer';
import TabsMenu from 'src/components/Menus/TabsMenu';
import Tooltip from 'src/components/Layouts/Tooltip';
import { CircularProgress } from '@mui/material';
import { createNotification } from 'src/utils/createNotification';
import { createUseStyles } from 'react-jss';
import { filterArray, getDayName, isKey } from 'src/utils/useFunctions';
import { getUserRole } from 'src/utils/useUser';
import { isCypress } from '../../../utils/useCypress';
import { setChildCardModal, setClubDetailModal, setConfirmModal } from '../../../store/actions/modals.actions';
import { setClubItems } from 'src/store/actions/club.actions';
import { setFilterParams } from 'src/store/actions/filters.actions';
import { setIsFiltersVisible } from 'src/store/actions/layout.actions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useEffect } from 'src/utils/useEffect';
import { useNavigate } from 'react-router';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: any) => ({
  root: {
    borderRadius: "10px",
    backgroundColor: theme.colors.white,
    width: "800px",
    maxWidth: '90vw',
    overflow: "auto",
    padding: "20px",
    margin: "20px",
    maxHeight: 'calc(100vh - 40px)',
  },
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    '& p': {
      fontWeight: "bold",
      marginBottom: "0",
    },
  },
  body: {
    marginTop: "20px",
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'auto',
    '& table': {
      width: '100%',
      '& > thead': {
        '& > tr': {
          '& > th': {
            textAlign: 'left',
          },
        },
      },
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: 'flex-end',
    marginTop: '32px',
    gap: '10px',
  },
  tabsWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    justifyContent: 'center',
    position: 'sticky',
    top: '0',
    zIndex: theme.zIndex.tabsWrapper,
    '& > div': {
      width: '100%',
      backgroundColor: theme.colors.white,
    },
    paddingBottom: '16px',
  },
  itemName: {
    fontWeight: 'bold',
    color: theme.colors.primaryBlue[500],
    cursor: 'pointer',
  },
  limitInput: {
    width: '100px',
  },
  remove: {
    cursor: 'pointer',
    color: theme.colors.primaryBlue[500],
    transition: 'color 0.25s',
    '&:hover': {
      color: theme.colors.primaryBlue[600],
    },
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(238,238,238,0.5)',
  },
  spinner: {
    '& svg': {
      color: theme.colors.primaryBlue[500]
    }
  },
  teacherPhoto: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    maxWidth: '36px',
    maxHeight: '36px', 
    borderRadius: '100%',
    backgroundColor: theme.colors.white,
    borderWidth: '3px',
    borderStyle: 'solid',
    borderColor: theme.colors.white,
    '& > div': {
      borderWidth: '0px',
    },
  },
  table: {
    width: '100%',
    maxWidth: '100%',
    '& > tbody': {
      '& > tr': {
        '& > td': {
          fontSize: '14px',
          '&:first-of-type': {
            width: '150px',
            '& > span': {
              display: 'inline-flex',
              gap: '8px',
              alignItems: 'center',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              color: theme.colors.grey[555],
              '& > svg': {
                width: '16px',
                height: '16px',
              },
            },
          },
          '&:last-of-type': {
            display: 'inline-flex',
            gap: '8px',
            alignItems: 'center',
            paddingLeft: '8px',
            maxWidth: 'calc(100% - 8px)',
          },
          '& a': {
            maxWidth: '100%',
            color: theme.colors.primaryBlue[500],
            cursor: 'pointer',
            transition: 'color 0.25s',
            fontWeight: 'bold',
            textDecoration: 'none',
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
            '&:hover': {
              color: theme.colors.primaryBlue[600],
            },
          },
          '& > small': {
            fontSize: '12px',
          },
        },
      },
    },
  },
  applicationsTables: {
    '& > thead': {
      '& > tr': {
        '& > th': {
          whiteSpace: 'nowrap',
          '& > span': {
            display: 'inline-flex',
            gap: '8px',
            alignItems: 'center',
            fontWeight: '700',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            color: theme.colors.grey[555],
            '& > svg': {
              width: '16px',
              height: '16px',
            },
          },
        },
      },
    },
    '& > tbody': {
      '& > tr': {
        '& > td': {
          width: '25%',
          fontSize: '14px',
          '& > div': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '& > span': {
              display: 'block',
              maxWidth: '10vw',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          '& > small': {
            fontSize: '12px',
          },
          '& > em': {
            fontStyle: 'initial',
            padding: '4px 12px',
            borderRadius: '24px',
            fontSize: '12px',
            '&.state1': {
              backgroundColor: theme.colors.systemOrange[200],
              color: theme.colors.systemOrange[500],
            },
            '&.state2': {
              backgroundColor: theme.colors.systemGreen[200],
              color: theme.colors.systemGreen[500],
            },
            '&.state3': {
              backgroundColor: theme.colors.systemRed[200],
              color: theme.colors.systemRed[500],
            },
          },
        },
      },
    },
  },
  cancelButton: {
    cursor: 'pointer',
    width: '32px',
    height: '32px',
    '& > svg': {
      color: theme.colors.primaryBlue[250],
      height: '24px',
      transition: 'color 0.25s',
    },
    '&:hover':{
      '& > svg': {
        color: theme.colors.primaryBlue[500],
      },
    },
  },
  childImage: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '24px',
    height: '24px',
    minWidth: '24px',
    '& > div': {
      width: '24px',
      height: '24px',
      borderRadius: '100%',
      position: 'relative',
    },
  },
  showToggleWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '0.5rem',
    backgroundColor: theme.colors.white,
  },
  showToggle: {
    color: theme.colors.primaryBlue[500],
    cursor: "pointer",
    fontWeight: 'bold',
    textTransform: 'uppercase',
    transition: 'color 0.25s',
    '&:hover': {
      color: theme.colors.primaryBlue[600],
    },
  },
  childWrapper: {
    cursor: 'pointer',
    '& > span': {
      color: theme.colors.primaryBlue[500],
      fontWeight: '600',
    },
  },
  paymentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '& > button': {
      '& > svg': {
        color: theme.colors.primaryBlue[500],
        width: '24px',
        height: '24px',
      },
    }
  },
  paymentInfoWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  applicationForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    '& > div': {
      boxShadow: 'none',
    },
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    borderWidth: '3px',
    borderStyle: 'solid',
    borderColor: theme.colors.chip,
    borderRadius: '6px',
    padding: '20px',
    gap: '16px',
    '& > span': {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      fontSize: '28px',
      fontWeight: '600',
    },
  },
  selectedChildImage: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '60px',
    height: '60px',
    minWidth: '60px',
    '& > div': {
      width: 'inherit',
      height: 'inherit',
      borderRadius: '100%',
      position: 'relative',
    },
  },
}));

const ClubDetailModal: React.FunctionComponent = () => {

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const browserData = useAppSelector((state: any) => state.browser);
  const configurationData = useAppSelector((state: any) => state.configuration).configuration;
  const clubsData = useAppSelector((state: any) => state.club);
  const dataData = useAppSelector((state: any) => state.data);
  const languageData = useAppSelector((state: any) => state.language);
  const modalsData = useAppSelector((state: any) => state.modals);
  const userData = useAppSelector((state: any) => state.user);
  const clubsService = useAppSelector((state: any) => state.services).clubsService;

  const isValid = modalsData.clubDetailModal.clubID === null ? false : true;
  const clubID = isValid ? modalsData.clubDetailModal.clubID : null;
  const defaultView = modalsData.clubDetailModal.view ? modalsData.clubDetailModal.view : "basic";
  const isClubExist = clubsData.items.filter((item: any) => item.clubID === clubID).length === 1;
  const clubTempData = useMemo(() => { return (isValid ? (isClubExist ? clubsData.items.find((item: any) => item.clubID === clubID) : {}) : {}); }, [clubID, clubsData.items, isClubExist, isValid]);
  const clubData = useMemo(() => { return {...clubTempData, pricePerLesson: clubTempData.price / clubTempData.countLessons}; }, [clubTempData]);

  const currencyID = clubData.currencyID;
  const getCurrency = currencyID ? (configurationData.currencies.filter((currency: any) => currency.currencyID === currencyID).length === 0 ? [] : configurationData.currencies.find((currency: any) => currency.currencyID === currencyID)) : [];
  
  const getChildData = useCallback((childID: any) => {
    return dataData.children.filter((item: any) => item.childID === childID).length === 0 ? [] : dataData.children.find((item: any) => item.childID === childID);
  }, [dataData.children]);

  const getSchoolData = useCallback((schoolID: any) => {
    return dataData.schools.filter((item: any) => item.schoolID === schoolID).length === 0 ? [] : dataData.schools.find((item: any) => item.schoolID === schoolID);
  }, [dataData.schools]);

  const getUserData = (userID: any) => {
    return dataData.users.filter((item: any) => item.userID === userID).length === 0 ? dataData.users.find((item: any) => item.userID === -1) : dataData.users.find((item: any) => item.userID === userID);
  };
  
  const clubChildrenAll = clubData.children;

  const clubChildren = useMemo(() => { return (Object.keys(clubData).length > 0 ? (clubChildrenAll !== undefined ? (clubChildrenAll.length > 0 ? clubChildrenAll.filter((item: any) => getChildData(item.childID).length !== 0) : []) : []) : []); }, [clubData, clubChildrenAll, getChildData]);

  const childrenWithNoAccess = filterArray(clubChildrenAll, clubChildren, "childID");

  const stateOrder: any = { 1: 1, 2: 2, 3: 3 };

  clubChildren.sort((a: any, b: any) => {
    if (a.state === b.state) {
      return a.clubApplicationID - b.clubApplicationID;
    }
    return stateOrder[a.state] - stateOrder[b.state];
  });

  const applicationFormRef: any = useRef(null);
  const applicationFormClientWidth = (applicationFormRef.current && applicationFormRef.current.clientWidth) ? applicationFormRef.current.clientWidth : 0;

  const applicationChildren = clubChildren.filter((item: any) => item.state !== 3).map((item: any) => { return item.childID; });

  const availableChildren = dataData.children.filter((item: any) => !item.isArchived && item.isInAnyActiveClass).map((item: any) => { 
    if(applicationChildren.indexOf(item.childID) === -1 && item.schoolsID.indexOf(clubData.schoolID) !== -1) {
      return { ...item, image: item.photo.thumbLink};
    } else {
      return null;
    }
  }).filter((item: any) => item !== null);

  const [state, setState] = useStates({
    isLoading: false,
    selectedChildren: availableChildren[0],
    applicationFormWidth: applicationFormClientWidth,
    viewMode: defaultView,
  });

  const tabsItems: any = [
    {
      name: 'club_basic',
      value: 'basic',
      isEnabled: true,
    },
    {
      name: 'club_applications',
      value: 'applications',
      badge: clubChildrenAll.length,
      badgeType: 'primary',
      isEnabled: clubChildrenAll.length > 0,
    },
    {
      name: 'club_application_add',
      value: 'addApplication',
      isEnabled: !state.isLoading && getUserRole(userData.userObject.roleType) === "parent" && clubData.enableEnrollment && availableChildren.length > 0,
    },
  ];

  const classes = useStyles();

  const setViewMode = useCallback((value: any) => {
    setState("viewMode", value);
  }, [setState]);
  
  const onCloseModal = () => {
    const settings = {
      isOpen: false,
      clubID: null,
      view: null,
    };
    dispatch(setClubDetailModal(settings));
  };

  const handleClose = (e: any) => {
    e.stopPropagation();
    onCloseModal();
  };

  const setSelectedChildren = (value: any) => {
    setState("selectedChildren", value);
  };

  const handleCreateApplication = () => {
    const settings = {
      clubID: clubData.clubID,
      childID: state.selectedChildren.childID
    };
    clubsService && clubsService.createClubApplication(settings).then((result: any) => {
      if(result) {
        if(result.status === 201) {
          createNotification(t("club_application_created"), "success");
          const newClubs = clubsData.items.map((item: any) => {
            if(item.clubID === clubID) {
              return result.data;
            } else {
              return item;
            }
          }).filter((item: any) => item !== null);
          dispatch(setClubItems(newClubs));
          const getNewChildren = availableChildren.filter((item: any) => item.childID !== state.selectedChildren.childID);
          if(getNewChildren.length === 0) {
            setState("viewMode", "applications"); 
          } else {
            const getNewChild = getNewChildren[0];
            setState("selectedChildren", getNewChild);
          }
        } else {
          createNotification(t("club_application_not_created"), "error");
        }
      } else {
        createNotification(t("club_application_not_created"), "error");
      }
    }).catch((e: any) => {
      createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("club_application_not_created"), "error");
    });
  };

  const handleClickDelete = (clubApplicationID: any, childID: any) => {
    const onAccept = () => {
      const settings = {
        clubID: clubID,
        childID: childID
      };
      clubsService && clubsService.deleteClubApplication(clubApplicationID, settings).then((result: any) => {
        if(result) {
          if(result.status === 200) {
            createNotification(t("club_application_canceled"), "success");
            const newClubs = clubsData.items.map((item: any) => {
              if(item.clubID === clubID) {
                return result.data;
              } else {
                return item;
              }
            }).filter((item: any) => item !== null);
            dispatch(setClubItems(newClubs));
          } else {
            createNotification(t("club_application_not_canceled"), "error");
          }
        } else {
          createNotification(t("club_application_not_canceled"), "error");
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("club_application_not_canceled"), "error");
      });
    };
    const settings = {
      isOpen: true,
      title: t('club_application_cancel'),
      content: t('club_application_cancel_confirm'),
      onAccept: onAccept,
      onDecline: null,
    };
    dispatch(setConfirmModal(settings));
  };

  const handleOpenChildDetail = (childID: any) => {
    const settings = {
      isOpen: true,
      childID: childID,
    };
    dispatch(setChildCardModal(settings));
  };

  const handlePaymentID = (e: any, paymentID: any) => {
    e.stopPropagation();
    navigate('/finance');
    dispatch(setFilterParams({paymentID: paymentID}));
    setTimeout(() => {
      dispatch(setFilterParams({paymentID: paymentID}));
      dispatch(setIsFiltersVisible(true));
    }, 100);
  };

  useEffect(() => {
    if(clubChildren.length === 0 && state.viewMode === 'applications') {
      setState("viewMode", "basic");
    }
    if(availableChildren.length === 0 && state.viewMode === 'addApplication') {
      setState("viewMode", "basic");
    }
  }, [clubChildren, availableChildren, setState, state.viewMode], [clubChildren, availableChildren])

  useEffect(() => {
    setState("applicationFormWidth", applicationFormClientWidth);  
  }, [browserData.width, applicationFormClientWidth, setState], [browserData.width, applicationFormClientWidth, state.viewMode]);

  useEffect(() => {
    if(state.viewMode === "addApplication") {
      setState("selectedChildren", availableChildren[0]);
    }
  }, [availableChildren, setState, state.viewMode], [state.viewMode]);

  const selectedChildrenApplicationsCount = (state.selectedChildren && state.selectedChildren.childID) ? clubData.children.filter((item: any) => item.childID === state.selectedChildren.childID).length : 0;
  const isSelectedChildrenAlreadyHaveApplication = selectedChildrenApplicationsCount !== 0;

  return (
    <Modal 
      open={true}
      onClose={onCloseModal}
    >
      <div className={classes.root} data-cy={isCypress() ? "shopOrderDetailModal" : null}>
        <div className={classes.header}>
          <div className={classes.wrapper}>
            <p>{clubData.name}</p>
          </div>
          <CloseButton onClick={handleClose} dataCy="timesButton"/> 
        </div>
        <div className={classes.body}>
          {
            state.isLoading ? (
              <div className={classes.loading}>
                <CircularProgress className={classes.spinner}/>
              </div>
            ) : (
              <>
                {
                  Object.keys(clubData).length !== 0 ? (
                    <>
                      <div className={classes.tabsWrapper}>
                        <TabsMenu items={tabsItems} selected={state.viewMode} onSelect={setViewMode}/>
                      </div>
                      {
                        state.viewMode === 'basic' ? (
                          <table className={classes.table}>
                            <tbody>
                              {
                                clubData.description ? (
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="info-circle-outlined"/>
                                        {t('club_description')}
                                      </span>
                                    </td>
                                    <td>
                                    <Clamp
                                      lines={4}
                                      maxLines={100}
                                      withToggle
                                      showMoreElement={({ toggle }: any) => (
                                        <div className={classes.showToggleWrapper}>
                                          <span className={classes.showToggle} onClick={toggle}>
                                            {t('show_more')}
                                          </span>
                                        </div>
                                      )}
                                      showLessElement={({ toggle }: any) => (
                                        <div className={classes.showToggleWrapper}>
                                          <span className={classes.showToggle} onClick={toggle}>
                                            {t('show_less')}
                                          </span>
                                        </div>
                                      )}
                                    >
                                      {clubData.description}
                                    </Clamp>
                                    </td>
                                  </tr>
                                ) : null
                              }
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="school"/>
                                    {t('club_school')}
                                  </span>
                                </td>
                                <td>
                                  {getSchoolData(clubData.schoolID).name}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                 <span>
                                    <SVG src="rooms"/>
                                    {t('club_place')}
                                  </span>
                                </td>
                                <td>
                                  {clubData.place}
                                </td>
                              </tr>
                              {
                                clubData.teacherID ? (
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="user-circle-outlined"/>
                                        {t('club_teacher')}
                                      </span>
                                    </td>
                                    <td>
                                      <AuthenticatedImage className={classes.teacherPhoto} thumbLink={getUserData(clubData.teacherID).photo.thumbLink} dataCy="teacherPhoto"/>
                                      {getUserData(clubData.teacherID).displayName}
                                    </td>
                                  </tr>
                                ) : null
                              }
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="clock"/>
                                    {t('club_time')}
                                  </span>
                                </td>
                                <td>
                                  {t(getDayName(clubData.dayInWeek))}
                                  &nbsp;
                                  (
                                  {clubData.timeFrom}
                                  &nbsp;-&nbsp;
                                  {clubData.timeTo}
                                  )
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="calendar"/>
                                    {t('club_date')}
                                  </span>
                                </td>
                                <td>
                                  {DateFormat(moment(clubData.dateFrom), "default", languageData, t)}
                                  &nbsp;-&nbsp;
                                  {DateFormat(moment(clubData.dateTo), "default", languageData, t)}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="hashtag"/>
                                    {t('club_lessons_count')}
                                  </span>
                                </td>
                                <td>
                                  {clubData.countLessons}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="price-tag-outlined"/>
                                    {t('club_price')}
                                  </span>
                                </td>
                                <td>
                                  <Currency quantity={parseFloat(clubData.priceTotal)} currency={getCurrency.iso}/>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <span>
                                    <SVG src="users"/>
                                    {t('club_capacity')}
                                  </span>
                                </td>
                                <td>
                                  {t('club_capacity_info_min', {min: clubData.childrenMin, minChildText: clubData.childrenMin === 1 ? (t('child')).toLowerCase() : (clubData.childrenMin === 2 || clubData.childrenMin === 3 || clubData.childrenMin === 4) ? (t('children')).toLowerCase() : (t('children_more')).toLowerCase()})}
                                  &nbsp;
                                  {clubData.childrenMax > 0 ? (t('club_capacity_info_max', {max: clubData.childrenMax, maxChildText: clubData.childrenMax === 1 ? (t('child')).toLowerCase() : (clubData.childrenMax === 2 || clubData.childrenMax === 3 || clubData.childrenMax === 4) ? (t('children')).toLowerCase() : (t('children_more')).toLowerCase()})) : ''}
                                </td>
                              </tr>
                              {
                                getUserRole(userData.userObject.roleType) !== "parent" ? (
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="eye"/>
                                        {t('club_visible_for_parent')}
                                      </span>
                                    </td>
                                    <td>
                                      {clubData.visibleForParent ? t('yes') : t('no')}
                                    </td>
                                  </tr>
                                ) : null
                              }
                              {
                                getUserRole(userData.userObject.roleType) !== "parent" ? (
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="pencil-new"/>
                                        {t('club_enable_enrollment')}
                                      </span>
                                    </td>
                                    <td>
                                      {clubData.enableEnrollment ? t('yes') : t('no')}
                                    </td>
                                  </tr>
                                ) : null
                              }
                            </tbody>
                          </table>
                        ) : state.viewMode === 'applications' ? (
                          <table className={classes.applicationsTables}>
                            <thead>
                              <tr>
                                <th>
                                  <span>
                                    <SVG src="user-circle-outlined"/>
                                    {t('club_child')}
                                  </span>
                                </th>
                                <th>
                                  <span>
                                    <SVG src="info-circle-outlined"/>
                                    {t('club_application_status')}
                                  </span>
                                </th>
                                <th>
                                  <span>
                                    <SVG src="price-tag-outlined"/>
                                    {t('club_application_price')}
                                  </span>
                                </th>
                                <th>
                                  <span>
                                    <SVG src="hashtag"/>
                                    {t('club_application_lessons_count')}
                                  </span>
                                </th>
                                {
                                  getUserRole(userData.userObject.roleType) === "parent" ? (
                                    <th/>
                                  ) : null
                                }
                              </tr>
                            </thead>
                            <tbody>
                              {
                                clubChildren.map((itemData: any, key: any) => {
                                  return getChildData(itemData.childID).length !== 0 ? (
                                    <tr key={`k_${key}`}>
                                      <td>
                                        <div className={classes.childWrapper} onClick={() => handleOpenChildDetail(itemData.childID)}>
                                          <AuthenticatedImage className={classes.childImage} thumbLink={getChildData(itemData.childID).photo.thumbLink}/>
                                          <span>{getChildData(itemData.childID).displayName}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <em className={"state" + itemData.state}>
                                          {itemData.stateText}
                                        </em>
                                      </td>
                                      <td>
                                        <div className={classes.paymentWrapper}>
                                          <div className={classes.paymentInfoWrapper}>
                                            <Currency quantity={parseFloat(itemData.priceTotal)} currency={getCurrency.iso}/>
                                            <small>(<Currency quantity={parseFloat(itemData.pricePerLesson)} currency={getCurrency.iso}/>&nbsp;{t('club_price_per_lesson').toLowerCase()})</small>
                                          </div>
                                          {
                                             (getUserRole(userData.userObject.roleType) === "parent" && itemData.paymentID && itemData.state === 2) ? (
                                              <IconButton tooltip={t('payment_detail')} onClick={(e: any) => handlePaymentID(e, itemData.paymentID)}>
                                                <SVG src="invoice"/>
                                              </IconButton>
                                             ) : null
                                          }
                                        </div>
                                      </td>
                                      <td>
                                        {itemData.countLessons}
                                      </td>
                                      {
                                        getUserRole(userData.userObject.roleType) === "parent" ? (
                                          <>
                                            {
                                              itemData.state === 1 ? (
                                                <td>
                                                  <IconButton className={classes.cancelButton} onClick={() => handleClickDelete(itemData.clubApplicationID, itemData.childID)}>
                                                    <SVG src="close"/>
                                                  </IconButton>
                                                </td>
                                              ) : (
                                                <td/>
                                              )
                                            }
                                          </>
                                        ) : null
                                      }
                                    </tr>
                                  ) : null;
                                })
                              }
                              {
                                childrenWithNoAccess.map((itemData: any, key: any) => (
                                  <tr key={`k_${key}`}>
                                  <td>
                                    <div>
                                      <img className={classes.childImage} src="/resources/images/user.svg" alt={t('child')}/>
                                      <Tooltip title={t('child_twigsee_info')}><span>{t('child_twigsee')}</span></Tooltip>
                                    </div>
                                  </td>
                                  <td>
                                    <em className={"state" + itemData.state}>
                                      {itemData.stateText}
                                    </em>
                                  </td>
                                  <td>
                                    <div className={classes.paymentWrapper}>
                                      <div className={classes.paymentInfoWrapper}>
                                        <Currency quantity={parseFloat(itemData.priceTotal)} currency={getCurrency.iso}/>
                                        <small>(<Currency quantity={parseFloat(itemData.pricePerLesson)} currency={getCurrency.iso}/>&nbsp;{t('club_price_per_lesson').toLowerCase()})</small>
                                      </div>
                                      {
                                        (getUserRole(userData.userObject.roleType) === "parent" && itemData.paymentID && itemData.state === 2) ? (
                                          <IconButton tooltip={t('payment_detail')} onClick={(e: any) => handlePaymentID(e, itemData.paymentID)}>
                                            <SVG src="invoice"/>
                                          </IconButton>
                                        ) : null
                                      }
                                    </div>
                                  </td>
                                  <td>
                                    {itemData.countLessons}
                                  </td>
                                  {
                                    getUserRole(userData.userObject.roleType) === "parent" ? (
                                      <>
                                        {
                                          itemData.state === 1 ? (
                                            <td>
                                              <IconButton className={classes.cancelButton} onClick={() => handleClickDelete(itemData.clubApplicationID, itemData.childID)}>
                                                <SVG src="close"/>
                                              </IconButton>
                                            </td>
                                          ) : (
                                            <td/>
                                          )
                                        }
                                      </>
                                    ) : null
                                  }
                                </tr>
                                ))
                              }
                            </tbody>
                          </table>
                        ) : state.viewMode === "addApplication" ? (
                          <div className={classes.applicationForm} ref={applicationFormRef}>
                            <ChildrenListSelect currentChild={state.selectedChildren} childrenData={availableChildren} setCurrentChild={setSelectedChildren} width={state.applicationFormWidth} isDisabled={state.isLoading}/>
                            <div className={classes.summary}>
                              <span>
                                <AuthenticatedImage className={classes.selectedChildImage} thumbLink={getChildData(state.selectedChildren.childID).photo.thumbLink}/>
                                {getChildData(state.selectedChildren.childID).displayName}
                              </span>
                              <table className={classes.table}>
                                <tbody>
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="hashtag"/>
                                        {t('club_lessons_count')}
                                      </span>
                                    </td>
                                    <td>
                                      {clubData.countLessons}
                                    </td>
                                    <td/>
                                  </tr>
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="price-tag-outlined"/>
                                        {t('club_price')}
                                      </span>
                                    </td>
                                    <td>
                                      <Currency quantity={parseFloat(clubData.priceTotal)} currency={getCurrency.iso}/>
                                    </td>
                                    <td/>
                                  </tr>
                                  <tr>
                                    <td>
                                      <span>
                                        <SVG src="info-circle-outlined"/>
                                        {t('club_note')}
                                      </span>
                                    </td>
                                    <td>
                                      {t('club_note_approve')}.
                                      {
                                        clubData.countAcceptedApplications > clubData.childrenMax ? (
                                          <>&nbsp;{t('club_note_exceeded_applications_count')}.</>
                                        ) : null
                                      }
                                      {
                                        isSelectedChildrenAlreadyHaveApplication ? (
                                          <>&nbsp;{t('club_note_already_declined_application', {count: selectedChildrenApplicationsCount})}.</>
                                        ) : null
                                      }
                                    </td>
                                    <td>
                                      <NormalButton buttonType='primary' onClick={handleCreateApplication} disabled={state.selectedChildren === null} dataCy="createApplicationButton">
                                        {t('club_application_add')}
                                      </NormalButton>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null
                      }
                    </>
                  ) : null
                }
              </>
            )
          }
        </div>
      </div>
    </Modal>
  );
};

export default ClubDetailModal;